/* eslint-disable react/prop-types */
import { useMemo, useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert, Button } from "reactstrap";
import toastr from "toastr";
import { useGetClientBookingDetails } from "../../../api/booking";
import Spinners from "../../../components/Common/Spinner";
import { useSocket } from "../../../providers/SocketProvider";
import InviteFlowLayout from "../invite-workers/components/InviteFlowLayout";
import SuggestedWorkersStep1View from "../invite-workers/step1/SuggestedWorkersStep1View";
import { useReplacementSuggestedWorkers } from "../invite-workers/step1/useReplacementSuggestedWorkers";
import {
  isEligibleReplacementShift,
  isReplacementSmartMatchingStartedEvent,
  patchShiftFromSmartMatchingStartedSocket,
} from "../replacementInviteEligibility";

function buildReplacementShiftEntries(shiftPattern, socketShiftPatches = {}) {
  const full = Array.isArray(shiftPattern) ? shiftPattern : [];
  return full
    .map((pattern, i) => {
      const shiftIndex =
        typeof pattern.shiftIndex === "number" ? pattern.shiftIndex : i;
      const patch = socketShiftPatches[shiftIndex];
      return {
        pattern: patch ? { ...pattern, ...patch } : pattern,
        shiftIndex,
      };
    })
    .filter(({ pattern }) => isEligibleReplacementShift(pattern));
}

function FindReplacementsSuggestedBody({
  booking,
  bookingSlug,
  shiftEntries,
  refetch,
  onSocketShiftPatch,
}) {
  const {
    byShift,
    selected,
    inviteListByShift,
    inviteListSessionError,
    loadMore,
    loadMorePending,
    initialPending,
    toggleDate,
    selectAllDatesForWorker,
    addToInviteList,
    removeFromInviteList,
    inviteWorkersForShift,
    inviteMutation,
    listBusy,
    pendingAppendKey,
    pendingRejectKey,
    pendingRemoveKey,
    pendingInviteShiftIndex,
    shiftInviteMeta,
    runSmartMatchingForShift,
    startSmartMatchingMutation,
    pendingSmartMatchShiftIndex,
    handleSmartMatchingStarted,
  } = useReplacementSuggestedWorkers({
    booking,
    bookingSlug,
    onAfterInvite: () => refetch?.(),
  });

  const { socket, isConnected } = useSocket();

  useEffect(() => {
    if (!isConnected || !socket || !bookingSlug) return;

    const onSmartMatchingStarted = (data) => {
      if (!isReplacementSmartMatchingStartedEvent(data, bookingSlug)) return;

      const shiftIndex = Number(data.shiftIndex);
      const patch = patchShiftFromSmartMatchingStartedSocket(data);

      onSocketShiftPatch(shiftIndex, patch);
      handleSmartMatchingStarted(shiftIndex);

      toastr.info(
        `Your selection time has ended. Automatic smart matching started for shift ${shiftIndex + 1}.`
      );

      refetch?.();
    };

    socket.on("smart-matching-started", onSmartMatchingStarted);
    return () => {
      socket.off("smart-matching-started", onSmartMatchingStarted);
    };
  }, [
    isConnected,
    socket,
    bookingSlug,
    handleSmartMatchingStarted,
    onSocketShiftPatch,
    refetch,
  ]);

  useEffect(() => {
    if (!isConnected || !socket || !bookingSlug) return;

    const onSmartMatchingComplete = async () => {
      toastr.success("Smart matching completed.");
      await refetch?.();
    };

    socket.on("smart-matching-complete", onSmartMatchingComplete);
    return () => {
      socket.off("smart-matching-complete", onSmartMatchingComplete);
    };
  }, [isConnected, socket, bookingSlug, refetch]);

  return (
    <SuggestedWorkersStep1View
      shiftEntries={shiftEntries}
      showReject={false}
      inviteLayout="perShift"
      sendInvitesLabel="Send replacement invites"
      byShift={byShift}
      selected={selected}
      inviteListByShift={inviteListByShift}
      inviteListSessionError={inviteListSessionError}
      loadMore={loadMore}
      loadMorePending={loadMorePending}
      initialPending={initialPending}
      toggleDate={toggleDate}
      selectAllDatesForWorker={selectAllDatesForWorker}
      addToInviteList={addToInviteList}
      removeFromInviteList={removeFromInviteList}
      inviteWorkersForShift={inviteWorkersForShift}
      pendingInviteShiftIndex={pendingInviteShiftIndex}
      inviteMutation={inviteMutation}
      listBusy={listBusy}
      pendingAppendKey={pendingAppendKey}
      pendingRejectKey={pendingRejectKey}
      pendingRemoveKey={pendingRemoveKey}
      shiftInviteMeta={shiftInviteMeta}
      showRunSmartMatching
      onRunSmartMatchingForShift={runSmartMatchingForShift}
      startSmartMatchingMutation={startSmartMatchingMutation}
      pendingSmartMatchShiftIndex={pendingSmartMatchShiftIndex}
    />
  );
}

export default function ClientFindReplacements() {
  const { bookingSlug } = useParams();
  const [socketShiftPatches, setSocketShiftPatches] = useState({});

  const {
    data: bookingRes,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetClientBookingDetails(bookingSlug);

  const booking = useMemo(
    () => bookingRes?.data?.data ?? bookingRes?.data ?? bookingRes,
    [bookingRes]
  );

  const onSocketShiftPatch = useCallback((shiftIndex, patch) => {
    setSocketShiftPatches((prev) => ({
      ...prev,
      [shiftIndex]: { ...prev[shiftIndex], ...patch },
    }));
  }, []);

  const shiftEntries = useMemo(
    () => buildReplacementShiftEntries(booking?.shiftPattern, socketShiftPatches),
    [booking?.shiftPattern, socketShiftPatches]
  );

  useEffect(() => {
    document.title = "Find replacements | Client";
  }, []);

  if (isLoading && !booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Spinners setLoading={() => {}} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger">
            Could not load booking. {error?.message || ""}
          </Alert>
          <Button color="primary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="warning">Booking not found.</Alert>
        </div>
      </div>
    );
  }

  return (
    <InviteFlowLayout
      booking={booking}
      bookingSlug={bookingSlug}
      hideSteps
      breadcrumbTitle="Find replacements"
      breadcrumbItem="Suggest and invite workers"
    >
      {!shiftEntries.length ? (
        <>
          <Alert color="info" className="mb-3">
            No shifts on this booking are open for replacement invites right now. This
            page is only available when a shift has smart matching in replacement mode, with
            replacement and manual invite options configured.
          </Alert>
          <Button
            tag={Link}
            color="primary"
            to={`/client/bookings/${bookingSlug}/workers-management`}
          >
            Back to worker management
          </Button>
        </>
      ) : (
        <>
          <FindReplacementsSuggestedBody
            booking={booking}
            bookingSlug={bookingSlug}
            shiftEntries={shiftEntries}
            refetch={refetch}
            onSocketShiftPatch={onSocketShiftPatch}
          />
          {isFetching && !isLoading && (
            <div className="small text-muted mt-2">Refreshing booking…</div>
          )}
        </>
      )}
    </InviteFlowLayout>
  );
}
